import { setWorkerUrl } from "maplibre-gl";
import catchyMapsLightStyle from "@catchy/assets/map-styles/catchy-maps-light.json";
import catchyMapsDarkStyle from "@catchy/assets/map-styles/catchy-maps-dark.json";
import catchyMapsHybridStyle from "@catchy/assets/map-styles/catchy-maps-hybrid.json";
import {
  catchyMapsConsumerPresentation,
  catchyMapsTileJsonUrl,
} from "./catchyMapsBasemapConfig.js";

const maplibreGlyphsUrl =
  process.env.NEXT_PUBLIC_MAPLIBRE_GLYPHS_URL ||
  "/map-glyphs/{fontstack}/{range}.pbf";
const maplibreWorkerUrl =
  process.env.NEXT_PUBLIC_MAPLIBRE_WORKER_URL || "/maplibre-gl-worker.mjs";

// MapLibre defaults maxCanvasSize to 4096x4096, which silently clamps the
// effective pixel ratio on wide DPR=2 displays once the canvas exceeds ~2048px
// in either dimension. That causes marker fidelity to drop at specific browser
// widths on the full-screen maps. 8192 matches the observed WebGL limits on the
// target machines and keeps DPR intact on large desktop windows.
export const maplibreMaxCanvasSize = [8192, 8192];
// The live transport maps are useful for city-scale orientation, but there is
// no additional map detail above this level and deeper zooms make accidental
// scroll/pinch zooms disorienting.
export const mainMapMaxZoom = 18;

export function createCatchyMap(MapConstructor, options) {
  // MapLibre 6 resolves its separate module worker relative to import.meta.url.
  // Turbopack rewrites that URL to the main MapLibre chunk, so explicitly point
  // it at the version-matched worker copied into each app's public directory.
  setWorkerUrl(maplibreWorkerUrl);

  const map = new MapConstructor({
    // Styles are authored and checked in with the app. Keeping validation in
    // development gives useful authoring errors, while production avoids the
    // validation work and lets bundlers omit its code.
    validateStyle: process.env.NODE_ENV !== "production",
    // Attribution remains required by our tile providers, but it is rendered
    // by the product UI rather than injected by MapLibre so its placement is
    // entirely under our control.
    attributionControl: false,
    // The default retains five viewport-sized zoom levels per source. Three
    // keeps panning responsive without holding as much vector-tile memory on
    // long-lived mobile map sessions.
    maxTileCacheZoomLevels: 3,
    maxCanvasSize: maplibreMaxCanvasSize,
    ...options,
  });

  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    window.__CATCHY_MAP__ = map;
  }

  return map;
}

const regularStyleUrlEnv = process.env.NEXT_PUBLIC_MAPLIBRE_STYLE_REGULAR_URL;
const darkStyleUrlEnv = process.env.NEXT_PUBLIC_MAPLIBRE_STYLE_DARK_URL;
const satelliteStyleUrlEnv =
  process.env.NEXT_PUBLIC_MAPLIBRE_STYLE_SATELLITE_URL;

const regularTilesUrlEnv = process.env.NEXT_PUBLIC_MAPLIBRE_STYLE_REGULAR_TILES;
const darkTilesUrlEnv = process.env.NEXT_PUBLIC_MAPLIBRE_STYLE_DARK_TILES;
const satelliteTilesUrlEnv =
  process.env.NEXT_PUBLIC_MAPLIBRE_STYLE_SATELLITE_TILES;

const stadiaMapsApiKey = process.env.NEXT_PUBLIC_STADIA_MAPS_API_KEY;
const localStyleCacheBust =
  process.env.NEXT_PUBLIC_MAPLIBRE_LOCAL_STYLE_VERSION || "20260604k";

const catchyMapsStyleTemplates = {
  regular: catchyMapsLightStyle,
  dark: catchyMapsDarkStyle,
  satellite: catchyMapsHybridStyle,
};
const catchyMapsSpriteUrl = "/map-sprites/catchy-maps-icons";

const defaultRegularTilesUrl =
  "https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png";
const defaultDarkTilesUrl =
  "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png";
const defaultSatelliteTilesUrl =
  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

const cartoAttribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>';

const esriAttribution =
  "Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

function withOptionalStadiaApiKey(styleUrl) {
  const trimmedUrl = String(styleUrl || "").trim();

  if (!trimmedUrl) {
    return null;
  }

  if (!stadiaMapsApiKey) {
    return trimmedUrl;
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    return trimmedUrl;
  }

  if (!parsedUrl.hostname.endsWith("stadiamaps.com")) {
    return trimmedUrl;
  }

  parsedUrl.searchParams.set("api_key", stadiaMapsApiKey);
  return parsedUrl.toString();
}

function withLocalStyleCacheBust(styleUrl) {
  const trimmedUrl = String(styleUrl || "").trim();

  if (!trimmedUrl) {
    return null;
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(trimmedUrl, "http://localhost");
  } catch {
    return trimmedUrl;
  }

  const isLocalStyle =
    trimmedUrl.startsWith("/") ||
    parsedUrl.hostname === "localhost" ||
    parsedUrl.hostname === "127.0.0.1";

  if (!isLocalStyle) {
    return trimmedUrl;
  }

  parsedUrl.searchParams.set("v", localStyleCacheBust);

  if (trimmedUrl.startsWith("/")) {
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  }

  return parsedUrl.toString();
}

function getConfiguredStyleUrl(kind) {
  if (kind === "satellite") {
    return withLocalStyleCacheBust(
      withOptionalStadiaApiKey(
        satelliteStyleUrlEnv,
      ),
    );
  }

  if (kind === "dark") {
    return withLocalStyleCacheBust(
      withOptionalStadiaApiKey(darkStyleUrlEnv),
    );
  }

  return withLocalStyleCacheBust(
    withOptionalStadiaApiKey(regularStyleUrlEnv),
  );
}

function makeAbsoluteBrowserUrl(url) {
  if (!url || /^https?:\/\//i.test(url) || typeof window === "undefined") {
    return url;
  }

  // `URL#toString()` encodes MapLibre's `{fontstack}` and `{range}` template
  // placeholders. Resolve root-relative public assets manually so glyph URLs
  // remain valid MapLibre templates.
  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }

  return new URL(url, window.location.origin)
    .toString()
    .replaceAll("%7B", "{")
    .replaceAll("%7D", "}");
}

function getCatchyMapsStyle(kind) {
  const style = JSON.parse(JSON.stringify(catchyMapsStyleTemplates[kind]));

  style.metadata = {
    ...style.metadata,
    "catchy:consumer-presentation": catchyMapsConsumerPresentation,
  };

  // The authored style keeps a relative TileJSON URL so it also works from the
  // public release prefix. A bundled style object has no source URL context,
  // so bind the compatibility pointer explicitly before MapLibre loads it.
  // Sprites and glyphs remain small same-origin app assets.
  if (style.sources?.basemap?.type === "vector") {
    delete style.sources.basemap.tiles;
    style.sources.basemap.url = catchyMapsTileJsonUrl;
  }
  if (style.sprite) style.sprite = makeAbsoluteBrowserUrl(catchyMapsSpriteUrl);
  style.glyphs = makeAbsoluteBrowserUrl(maplibreGlyphsUrl);

  return style;
}

function buildRasterStyle({ id, tiles, attribution, backgroundColor }) {
  return {
    version: 8,
    name: id,
    glyphs: maplibreGlyphsUrl,
    sources: {
      [id]: {
        type: "raster",
        tiles: [tiles],
        tileSize: 256,
        attribution,
      },
    },
    layers: [
      {
        id: `${id}-background`,
        type: "background",
        paint: {
          "background-color": backgroundColor,
        },
      },
      {
        id,
        type: "raster",
        source: id,
      },
    ],
  };
}

function getRasterFallbackStyle(kind) {
  if (kind === "satellite") {
    return buildRasterStyle({
      id: "catchy-satellite",
      tiles: satelliteTilesUrlEnv || defaultSatelliteTilesUrl,
      attribution: esriAttribution,
      backgroundColor: "#111827",
    });
  }

  if (kind === "dark") {
    return buildRasterStyle({
      id: "catchy-dark",
      tiles: darkTilesUrlEnv || defaultDarkTilesUrl,
      attribution: cartoAttribution,
      backgroundColor: "#0f172a",
    });
  }

  return buildRasterStyle({
    id: "catchy-regular",
    tiles: regularTilesUrlEnv || defaultRegularTilesUrl,
    attribution: cartoAttribution,
    backgroundColor: "#f8fafc",
  });
}

export function getMaplibreStyle({
  preferSatellite = false,
  isDark = false,
} = {}) {
  const kind = preferSatellite ? "satellite" : isDark ? "dark" : "regular";
  const configuredStyleUrl = getConfiguredStyleUrl(kind);

  if (configuredStyleUrl) {
    return configuredStyleUrl;
  }

  return getCatchyMapsStyle(kind);
}

export function isMaplibreSupported() {
  if (typeof document === "undefined") {
    return false;
  }

  const canvas = document.createElement("canvas");
  return Boolean(
    canvas.getContext("webgl2") ||
    canvas.getContext("webgl") ||
    canvas.getContext("experimental-webgl"),
  );
}
